"use client";

import { useAuth } from "@clerk/nextjs";
import { Link } from "@radix-ui/themes";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRightIcon } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";

import { useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

const ProgramsHero = () => {
	const containerRef = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
	});
	const { isSignedIn } = useAuth();
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	const scaleDimensions = () => {
		return [1.05, 1];
	};

	const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

	const rotate = useTransform(scrollYProgress, [0, 1], [20, 0]);
	const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions());
	const translate = useTransform(scrollYProgress, [0, 1], [0, -100]);

	return (
		<div className="flex flex-col items-center border-b px-4 py-10 tablet:pt-0 tablet:pb-20">
			<div
				className="relative flex flex-col items-center justify-center p-2 md:py-14"
				ref={containerRef}
			>
				<div
					className="relative flex w-full flex-col items-center justify-center gap-16 md:gap-12"
					style={{
						perspective: "1000px",
					}}
				>
					<motion.div
						style={{
							translateY: isMobile ? 0 : translate,
						}}
						className="!translate-0 tablet:translate-full mx-auto -mt-4 flex max-w-5xl flex-col items-center gap-6 tablet:gap-10 text-center"
					>
						<span className="w-fit rounded-full bg-orange-200 px-3.5 py-1 font-medium text-orange-900">
							Early Access
						</span>

						<h2 className="font-bold tablet:text-5xl text-2xl">
							The First Evidence-based Client Program Generator
						</h2>

						<Link
							href="/programs/new"
							className="group mt-4 flex items-center gap-1 rounded-full bg-sky-500 px-4 tablet:px-5 py-2 tablet:py-2.5 font-medium text-white shadow-lg transition hover:bg-sky-600"
						>
							Create Your First Program{" "}
							<ChevronRightIcon className="-mr-1.5 size-5 transition-transform group-hover:translate-x-0.5" />
						</Link>

						<p className="tablet:text-xl text-lg">
							This technology is revolutionary. Create evidence-based,
							personalized routines in seconds.
						</p>
					</motion.div>

					<motion.div
						style={{
							rotateX: rotate,
							scale,
						}}
						className="mx-auto tablet:block hidden w-full max-w-5xl rounded-[30px] border-4 border-[#6C6C6C] bg-[#222222] p-2 shadow-2xl md:p-6"
					>
						<div className="relative h-fit w-fit overflow-hidden rounded-2xl bg-gray-100 md:rounded-2xl dark:bg-zinc-900">
							<Image
								src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/programs/hero.gif`}
								alt="Programs Hero"
								unoptimized
								width={1000}
								height={593}
								className="object-contain"
							/>
						</div>
					</motion.div>
				</div>
			</div>
		</div>
	);
};

export default ProgramsHero;
